/**
 * Theme Context for managing application theme state
 * Provides theme switching functionality and persistence
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { UserPreferences } from '../types/profile';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  actualTheme: 'light' | 'dark'; // The actual applied theme (resolves 'system')
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first, then use default
    const stored = localStorage.getItem('theme') as Theme;
    return stored || defaultTheme;
  });

  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light');

  // Function to get the actual theme (resolve 'system' to light/dark)
  const getActualTheme = (themeValue: Theme): 'light' | 'dark' => {
    if (themeValue === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return themeValue;
  };

  // Apply theme to document
  const applyTheme = (themeValue: Theme) => {
    const resolvedTheme = getActualTheme(themeValue);
    setActualTheme(resolvedTheme);
    
    // Apply to document
    if (resolvedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Set theme and persist to localStorage
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
  };

  // Toggle between light and dark (system becomes light)
  const toggleTheme = () => {
    const newTheme = actualTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  // Initialize theme on mount
  useEffect(() => {
    applyTheme(theme);
  }, []);

  // Listen for system theme changes when theme is 'system'
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        applyTheme('system');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const value: ThemeContextType = {
    theme,
    actualTheme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Utility function to sync theme with user preferences
export function syncThemeWithPreferences(preferences: UserPreferences, setTheme: (theme: Theme) => void) {
  if (preferences.theme && preferences.theme !== localStorage.getItem('theme')) {
    setTheme(preferences.theme);
  }
}

// Utility function to cache preferences in localStorage
export function cachePreferencesInLocalStorage(preferences: UserPreferences) {
  try {
    localStorage.setItem('user_preferences', JSON.stringify(preferences));
    
    // Also cache individual preference items for quick access
    if (preferences.theme) {
      localStorage.setItem('theme', preferences.theme);
    }
    if (preferences.language) {
      localStorage.setItem('language', preferences.language);
    }
  } catch (error) {
    console.warn('Failed to cache preferences in localStorage:', error);
  }
}

// Utility function to get cached preferences from localStorage
export function getCachedPreferencesFromLocalStorage(): Partial<UserPreferences> | null {
  try {
    const cached = localStorage.getItem('user_preferences');
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.warn('Failed to get cached preferences from localStorage:', error);
    return null;
  }
}